/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from 'react';
import { Divider, Skeleton } from 'antd';
import {
  PhotoResolution,
  sessionType,
  VideoCallStatusUpdate,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { EVENT_STATUS } from '@/constants/device';
import MainPage from '../../MainPage';
import {
  GroupsIcon,
  CallIcon,
  ContactIcon,
  HSCalendar,
  SuccessCallIcon,
  DeclinedCallIcon,
  MissedCallStatusIcon,
  BusyCallIcon,
  ContactNewUIIcon,
  OutgoingCallIcon,
  IncomingCallIcon,
} from '../Common/Icons';
import { getDateFormat, getTimeFormat } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import {
  getProfilePicUrl,
  getUsername,
  truncateStringWith3Dots,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import { getContacts } from '../Schedule/Contacts';
import { exportCallActivityToPDF } from '@/utilities/helpers/fileHelper';
import ExportPdfButton from '@/Components/Common/ExportPdfButton';
import { HSCard, HSCardSkeleton } from '@/Components/Common/HSWidget';
import { UserImage } from '../Schedule/UserImage';
import { getProfilePics } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import { useSelector } from 'react-redux';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import moment from 'moment';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(timezone);

const LocalTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const ListCallActivity = (props) => {
  const {
    onlyContent,
    parentBread,
    selectedDate,
    setSelectedDate,
    contacts,
    userInfo,
    userHubsData,
    notifications,
    fetchingNotifications,
    scheduleList,
    timezone,
    loginUserTimeformat,
    fetchingScheduleList,
  } = props;
  const trans = useSelector(translator);
  const resourcesMap = useSelector(getProfilePics());
  const currentUser = useSelector(getCurrentUser);
  const header = parentBread || [];
  const userTimezone = timezone || LocalTimezone;

  const formatList = (notifications, scheduleList) => {
    const filteredNotifications = (notifications ?? []).filter(
      (event) =>
        event.message_type === 'call_status' &&
        (event.call_status__status === VideoCallStatusUpdate.missed ||
          event.call_status__status === VideoCallStatusUpdate.declined ||
          event.call_status__status === VideoCallStatusUpdate.busy ||
          event.call_status__status === VideoCallStatusUpdate.completed)
    );

    // Filter schedule events with event_type equal to "schedule"
    const filteredScheduleEvents = (scheduleList ?? []).filter(
      (event) => event.event_type === 'schedule'
    );

    // Merge filtered notifications and schedule events into a single array
    const mergedEvents = [...filteredNotifications, ...filteredScheduleEvents];

    const selectedDateTime = new Date(selectedDate);
    selectedDateTime.setDate(selectedDateTime.getDate() + 1);
    const now = new Date();

    let filteredList = mergedEvents.filter((event) => {
      const eventDate = new Date(event.event_timestamp);
      return (
        eventDate.getTime() <= selectedDateTime.getTime() && eventDate.getTime() <= now.getTime()
      );
    });

    // Sort the filtered list by event timestamp
    filteredList.sort((a, b) => new Date(a.event_timestamp) - new Date(b.event_timestamp));

    const list = filteredList.reduce((acc, event) => {
      const dateObj = getDateFormat(event.event_timestamp, userTimezone);
      const date = dateObj.dateFormat;
      if (!acc[date]) {
        acc[date] = [];
      }
      const duplicate = acc[date].some((e) => e.schedule_eventuuid === event.schedule_eventuuid);
      if (!duplicate) {
        acc[date].push(event);
      }
      return acc;
    }, {});
    const sortedList = Object.values(list).sort((a, b) => {
      return new Date(b[0]?.event_timestamp) - new Date(a[0]?.event_timestamp);
    });
    return sortedList;
  };

  const formattedList = useMemo(
    () => formatList(notifications, scheduleList),
    [notifications, scheduleList]
  );

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const exportToPDF = async () => {
    try {
      await exportCallActivityToPDF({
        trans,
        userInfo,
        currentUser,
        formattedList,
        userTimezone,
        loginUserTimeformat,
        contacts,
        userHubsData,
      });
    } catch (error) {
      console.error('Error exporting call activity to PDF:', error);
      // message.error(
      //   trans('EXPORT_PDF_ERROR') || 'Failed to export PDF. Please try again.'
      // );
    }
  };

  const contactImage = (contact) => {
    let displayName = getUsername(contact);
    const url = getProfilePicUrl(contact.useruuid, resourcesMap, PhotoResolution.R64);
    return <UserImage name={displayName} url={url} />;
  };

  const renderDateListItem = (item) => {
    let dateObj = getDateFormat(item?.event_timestamp, userTimezone);
    return (
      <div
        key={dateObj.dayDateformat}
        className={
          dateObj.dayDateformat.startsWith('Today')
            ? 'callactivity-list-date callactivity-list-today'
            : 'callactivity-list-date'
        }
      >
        <div className="callactivity-date">{dateObj.dayDateformat}</div>
      </div>
    );
  };

  const renderCallDirection = (item) => {
    if (item?.incoming !== undefined) {
      const invitedList = renderInvitedList(item.invited_contacts);
      if (invitedList.count > 2) {
        return null; // Do not render call direction if invited contacts are more than 2
      } else if (item?.incoming) {
        return (
          <span className="call-direction">
            <IncomingCallIcon />
          </span>
        );
      } else {
        return (
          <span className="call-direction">
            <OutgoingCallIcon />
          </span>
        );
      }
    }
    return null; // Don't render anything if the incoming information is not present
  };

  const renderInvitedList = (invitedUseruuid, event_name) => {
    const updatedInvitedUser =
      event_name === null ? invitedUseruuid.map((user) => user.useruuid) : invitedUseruuid;

    let invitedContacts = getContacts(updatedInvitedUser, [
      ...(contacts ?? []),
      ...(userHubsData ?? []),
      ...(userInfo ? [userInfo] : []),
    ]);

    const count = invitedContacts.length;

    const invitedContactsData = invitedContacts.slice(0, 9).map((contact, index) => (
      <div key={index} className="user-image-container">
        {contact && contactImage(contact)}
      </div>
    ));

    return { invitedContactsData, count };
  };

  const getCallStatusIcon = (buttonType, duration, eventStatus) => {
    const formatDuration = (duration) => {
      // Round up durations less than 60 seconds to one minute
      if (duration <= 60) {
        return '1 min';
      }

      const minutes = Math.ceil(duration / 60);
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;

      if (hours > 0) {
        return `${hours} hr ${remainingMinutes} min`;
      } else {
        return `${remainingMinutes} min`;
      }
    };

    // Reusable completed status JSX
    const completedStatusJSX = (
      <>
        <SuccessCallIcon />
        {!!duration && <span className="call-duration">{formatDuration(duration)}</span>}
      </>
    );

    // Check event_status first (for schedule events)
    if (eventStatus) {
      if (eventStatus === EVENT_STATUS.COMPLETED) {
        return completedStatusJSX;
      } else if (eventStatus === EVENT_STATUS.EXPIRED) {
        return <MissedCallStatusIcon />;
      } else {
        // Unknown event status - don't fall through to call status logic
        return null;
      }
    }

    // Fall back to call_status__status (for call notifications)
    if (buttonType === VideoCallStatusUpdate.completed) {
      return completedStatusJSX;
    } else {
      switch (buttonType) {
        case VideoCallStatusUpdate.missed:
          return <MissedCallStatusIcon />;
        case VideoCallStatusUpdate.declined:
          return <DeclinedCallIcon />;
        case VideoCallStatusUpdate.busy:
          return <BusyCallIcon />;
        default:
          return null;
      }
    }
  };

  const disabledFutureDate = (current) => {
    return current && current > moment().endOf('day');
  };

  const renderCallActivityList = () => {
    return (
      <div className="callactivity-list-container">
        {/* <div className="callactivity-actions">
          <SingleDatePicker
            DateFormat={DateFormat}
            timeZone={userTimezone}
            defaultValue={selectedDate}
            onDateSelect={handleDateSelect}
            disabledDate={disabledFutureDate}
            value={selectedDate ? dayjs(selectedDate).tz(userTimezone, true) : null}
          />
        </div> */}
        {fetchingNotifications || fetchingScheduleList ? (
          <div className="callactivite-data-list">
            <div className="callactivity-list-date">
              <div className="callactivity-date">
                <Skeleton.Input active size="small" style={{ width: 226 }} />
              </div>
            </div>
            <HSCardSkeleton />
          </div>
        ) : (
          formattedList.map((list, index) => (
            <div key={'callevents_day_' + index} className="callactivite-data-list">
              {renderDateListItem(list[0])}
              {list.map((item) => (
                <HSCard
                  key={item.message_id}
                  className={`callactivity-container ${
                    item.call_status__status === VideoCallStatusUpdate.missed ||
                    item.call_status__status === VideoCallStatusUpdate.declined
                      ? 'callactivity-grey-container'
                      : ''
                  }`}
                  content={
                    <>
                      <div className="callactivity-list-row1">
                        <div className="container-1">
                          <div title={userTimezone} className="callactivity-eventtime">
                            {getTimeFormat(
                              item.event_timestamp,
                              userTimezone,
                              loginUserTimeformat?.is24hoursformat
                            )}
                          </div>
                          {item.recurring ? (
                            <HSCalendar />
                          ) : (
                            <div
                              className="empty-action"
                              style={{ width: '30px', marginLeft: '10px' }}
                            ></div>
                          )}
                          <div
                            className={`${
                              item.event_type?.toLowerCase() === sessionType.zoom
                                ? 'call-activity-zoomeventtype'
                                : 'call-activity-hseventype'
                            }`}
                          >
                            {item.event_type?.toLowerCase() === sessionType.zoom
                              ? trans('ZOOMCALL')
                              : trans('VIDEOCALL')}
                          </div>
                          <div className="callactivity-eventname">{item?.event_name}</div>
                          {renderCallDirection(item)}
                        </div>
                        <div className="container-2" style={{ width: '250px' }}>
                          {getCallStatusIcon(
                            item?.call_status__status,
                            item?.duration,
                            item?.event_status
                          )}
                        </div>
                      </div>
                      <div className="callactivity-list-row2">
                        <div>
                          {item?.message_type === 'call_status' ? (
                            <ContactIcon className="contacts-icon" />
                          ) : (
                            <ContactNewUIIcon className="contact-icon" />
                          )}
                        </div>
                        {renderInvitedList(item.invited_contacts).count > 0 && (
                          <div className="invited-count">
                            {renderInvitedList(item.invited_contacts).count}
                          </div>
                        )}
                        <div className="invited-list">
                          {item?.invited_contacts &&
                            renderInvitedList(item?.invited_contacts, item?.event_name)
                              .invitedContactsData}
                        </div>
                        {item.group_name && item.groupuuid ? (
                          <>
                            <Divider className="groups-schedule-divider" type="vertical" />
                            <div className="group-schedule-information">
                              <div className="groups-schedule-typegroup">
                                <GroupsIcon className="groups-schedule-typegroup-icon" />
                                <span className="groups-schedule-typegroup-label">
                                  {trans('GROUPS')}
                                </span>
                              </div>
                              <span className="groups-schedule-selected-label">
                                {truncateStringWith3Dots(item.group_name, 70)}
                              </span>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </>
                  }
                />
              ))}
            </div>
          ))
        )}
      </div>
    );
  };

  const headerSection = () => {
    return (
      <div className="callactivity-header">
        <div className="callactivity-title">
          <div>
            <CallIcon className="callactivity-icon-newui" />
          </div>
          <div className="callactivity-text-newui">{trans('CALLACTIVITY')}</div>
        </div>
        <div className="callactivity-actions">
          <ExportPdfButton
            onClick={exportToPDF}
            disabled={formattedList.length === 0 || fetchingNotifications}
          />
        </div>
      </div>
    );
  };

  const render = (
    <div
      className={`list-callactivity-container page-content-wrapper ${
        formattedList.length === 0 ? 'new-list-callactivity-container page-content-wrapper' : ''
      }`}
    >
      {headerSection()}
      <div>
        {formattedList.length === 0 && !fetchingNotifications && !fetchingScheduleList ? (
          <div className="call-activity-empty-list-container">
            <div className="call-activity-empty-list-label">
              Your call history is empty. Once you make or receive calls, they’ll appear here.
            </div>
          </div>
        ) : (
          <div className="callactivity-content">{renderCallActivityList()}</div>
        )}
      </div>
    </div>
  );
  return onlyContent ? (
    render
  ) : formattedList.length === 0 && !fetchingNotifications && !fetchingScheduleList ? (
    render
  ) : (
    <MainPage header={header} noModals={true}>
      {render}
    </MainPage>
  );
};

export default ListCallActivity;
